import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CalculateRounded from '@mui/icons-material/CalculateRounded';
import CleaningServicesRounded from '@mui/icons-material/CleaningServicesRounded';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import FactCheckRounded from '@mui/icons-material/FactCheckRounded';
import RuleRounded from '@mui/icons-material/RuleRounded';
import QuestionAnswerRounded from '@mui/icons-material/QuestionAnswerRounded';
import SwapHorizRounded from '@mui/icons-material/SwapHorizRounded';
import {
  LandingContainer,
  Section,
  Container,
  AnimateOnScroll,
  CenteredHeader,
  SectionEyebrow,
  SectionTitle,
  SectionSubtitle,
  landingHeroStyles,
} from '../home/HomePage.styles';
import { SiteHeader } from '../../shared/components/SiteHeader';
import { Footer } from '../../shared/components/Footer';
import SEO from '../../shared/components/SEO';
import ConnectorGlobe from '../../shared/components/ConnectorGlobe';
import OfficeWindowAnimation from '../../shared/components/OfficeWindowAnimation';
import CapabilityShowcase from '../home/CapabilityShowcase';
import CoreCapabilities, { CtaRow, FaqList, SurfaceLink, WorkflowContrast } from './CoreCapabilities';
import { useScrollReveal } from '../../shared/components/useScrollReveal';
import { FAQ_REPOSITORY } from '../../shared/services/faqChatService';

// =============================================================================
// EXCEL PAGE (/excel)
// -----------------------------------------------------------------------------
// The secondary product surface: Restack for Excel, and how it works together
// with the PowerPoint add-in (the cross-app "pull the table onto the slide"
// moment is the hook). Copy lifted from docs/excel-plugin-onepager.md. Shares
// the marketing shell (SiteHeader + Footer + SEO + the warm landing styles)
// with /product so the two surfaces read as one product. Page spine matches
// /product: hero (headline, subhead, ONE primary CTA, proof, hero visual) ->
// problem to solution -> cross-app -> benefits -> how it works -> capabilities
// -> connectors -> who it is for -> FAQ -> risk reversal + closing CTA.
// House style: no em dashes; "model / deck / live / formula-driven / side panel".
// =============================================================================

const ACCENT = '#B83B1D';
const INK = '#2E140A';
const EXCEL_GREEN = '#217346';

// One offer, one action: the same label in the hero and in the closing band.
const PRIMARY_CTA_LABEL = 'See it on your own model';

const excelCapabilities = [
  {
    icon: <CalculateRounded />,
    title: 'Builds financial models',
    desc: 'DCF, three-statement, LBO, and operating models. Formula-driven and auditable, not hardcoded values you can\'t trace.',
  },
  {
    icon: <CleaningServicesRounded />,
    title: 'Cleans and restructures data',
    desc: 'Promote the real header, drop blank columns, reshape a messy export into something usable.',
  },
  {
    icon: <BarChartRounded />,
    title: 'Charts and pivot tables',
    desc: 'Native Excel charts and pivots from a plain-English request. No wizard, no range-wrangling.',
  },
  {
    icon: <FactCheckRounded />,
    title: 'Researches real numbers',
    desc: 'Pulls first-party sources and drops source notes into the cells it fills.',
  },
  {
    icon: <RuleRounded />,
    title: 'Checks its own work',
    desc: 'Reads results back after writing formulas, catching errors before you see them.',
  },
  {
    icon: <QuestionAnswerRounded />,
    title: 'Asks before it assumes',
    desc: 'A couple of quick tap-to-answer questions first, so it builds the right thing.',
  },
];

const crossAppSteps = [
  { n: '1', title: 'You ask, in PowerPoint', desc: '"Pull the Q3 revenue table from my open Excel model onto slide 4."' },
  { n: '2', title: 'Excel reads the numbers', desc: 'Your Excel pane reads the live model with no manual export step.' },
  { n: '3', title: 'PowerPoint builds the slide', desc: 'The deck lays out the table on your template, live in the pane.' },
];

const excelPrompts = [
  '"Build a 5-year DCF for revenue of 100M growing 8 percent, WACC 10 percent."',
  '"Add a sensitivity table for WACC versus terminal growth, base case in the center."',
  '"Clean this export: promote the real header row and remove the blank columns."',
  '"Pivot this transaction data by region and quarter, then chart it."',
  '"What is driving the gross margin change between 2023 and 2024?"',
];

const crossPrompts = [
  'In PowerPoint: "Pull the revenue build from my open Excel model onto slide 4."',
  'In Word: "Drop the returns summary from my open Excel model into the IC memo."',
];

// -----------------------------------------------------------------------------
// PROBLEM TO SOLUTION. The model cycle as it actually runs, then the same cycle
// with Restack in the workbook. The contrast is in the steps, not in numbers:
// there are no metrics here on purpose.
// -----------------------------------------------------------------------------
const workflowContrast = [
  {
    step: 'Source',
    before: 'Figures get keyed in from a PDF or an export, and the source ends up in a tab nobody opens again.',
    after: 'Restack pulls first-party sources and drops the source note into the cell it filled.',
  },
  {
    step: 'Build',
    before: "The DCF or the LBO gets rebuilt from the last deal's file, along with whatever was hardcoded in it.",
    after: 'It builds formula-driven and auditable, so any output traces back through the sheet.',
  },
  {
    step: 'Check',
    before: 'Broken references and stale ranges surface when the partner opens the file.',
    after: 'Restack reads results back after it writes formulas, so it catches the breaks before you see them.',
  },
  {
    step: 'Carry',
    before: 'The table is pasted into the deck, then pasted again every time the model moves.',
    after: 'Ask once and the live numbers move into the deck or the memo, laid out for that deliverable.',
  },
];

// -----------------------------------------------------------------------------
// FAQ. Pulled by id from FAQ_REPOSITORY so this page and the support widget
// serve the same answers. The `refund` entry carries the risk reversal.
// -----------------------------------------------------------------------------
const EXCEL_FAQ_IDS = [
  'what-is-restack',
  'how-works',
  'different-from-chatgpt',
  'research-quality',
  'powerpoint-versions',
  'security',
  'pricing',
  'collaboration',
  'getting-started',
  'refund',
];
const EXCEL_FAQ = EXCEL_FAQ_IDS
  .map((id) => FAQ_REPOSITORY.find((entry) => entry.id === id))
  .filter(Boolean);

const EXCEL_FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: EXCEL_FAQ.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: { '@type': 'Answer', text: answer },
  })),
};

// The risk reversal, verbatim from the entry the FAQ also renders.
const REFUND_COPY = FAQ_REPOSITORY.find((entry) => entry.id === 'refund')?.answer || '';

export default function ExcelPage() {
  const theme = useTheme();
  const hair = alpha(INK, 0.09);

  // Scroll-reveal. These marketing pages scroll INSIDE LandingContainer (not
  // the window) and IntersectionObserver is unreliable in that setup, so we use
  // a capture-phase scroll + resize listener running a getBoundingClientRect
  // check (the same approach SiteHeader and the solutions page use).
  // Scroll reveal for the animated blocks on this page.
  const isVisible = useScrollReveal();

  const eyebrowSx = {
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.68rem',
    fontWeight: 600,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: ACCENT,
  };

  return (
    <LandingContainer data-theme="light">
      <SEO
        title="Restack for Excel | The Model Engine for Private Equity"
        description="The model engine for Private Equity, in your live workbook. Restack builds DCF, three-statement, LBO, and operating models the way Private Equity expects them, formula-driven, with inputs cited and built to be committee-defensible."
        canonical="/excel"
        schema={EXCEL_FAQ_SCHEMA}
      />
      <style>{landingHeroStyles}</style>

      <SiteHeader offsetTop={0} />

      <Box component="main" id="main-content">
        {/* 1. HERO. Eyebrow + title + a short subtitle, then the single primary
            CTA with the Microsoft Store credential beside it, then ONE full Excel
            window with the Restack AI sidebar integrated inside it (the shared
            OfficeWindowAnimation, running the LBO scenario). */}
        <Section id="excel-hero" sx={{ pt: { xs: 11, md: 16 } }}>
          <Container>
            <AnimateOnScroll isVisible={isVisible('excel-hero-header')} data-animate-id="excel-hero-header">
              <CenteredHeader sx={{ mb: { xs: 3, md: 4 } }}>
                <SectionEyebrow>Restack for Excel</SectionEyebrow>
                <SectionTitle component="h1">
                  The model engine for Private Equity, in your <em>live workbook.</em>
                </SectionTitle>
                <SectionSubtitle>
                  DCF, three-statement, LBO, and operating models built the way Private Equity expects them:
                  formula-driven, inputs cited, built to survive the partner read. In your live workbook.
                </SectionSubtitle>
              </CenteredHeader>
              {/* The CTA sits outside CenteredHeader: that component forces
                  `margin: 0 auto` on every direct child, which would fight the
                  row's own vertical spacing. */}
              <CtaRow label={PRIMARY_CTA_LABEL} theme={theme} sx={{ mb: { xs: 6, md: 8 } }} />
            </AnimateOnScroll>

            {/* HERO VISUAL: the shared Office window, with the Restack AI sidebar
                integrated as a column inside it, running the LBO scenario. */}
            <AnimateOnScroll isVisible={isVisible('excel-hero-visual')} delay={0.1} data-animate-id="excel-hero-visual">
              <OfficeWindowAnimation app="excel" scenario="excel-lbo" />
            </AnimateOnScroll>
          </Container>
        </Section>

        {/* 2. PROBLEM TO SOLUTION. Where the hours go in the model cycle, and
            what changes with Restack in the workbook. */}
        <Section id="excel-rebuild">
          <Container>
            <AnimateOnScroll isVisible={isVisible('rebuild-header')} data-animate-id="rebuild-header">
              <CenteredHeader>
                <SectionEyebrow>The rebuild</SectionEyebrow>
                <SectionTitle component="h2">The same model, <em>rebuilt every deal</em>.</SectionTitle>
                <SectionSubtitle>
                  The structure barely changes between deals. The work of getting there never gets shorter.
                  Here is where the hours go, and what changes with Restack in the workbook.
                </SectionSubtitle>
              </CenteredHeader>
            </AnimateOnScroll>

            <WorkflowContrast rows={workflowContrast} accent={EXCEL_GREEN} theme={theme} hair={hair} isVisible={isVisible} />
          </Container>
        </Section>

        {/* 3. THE CROSS-APP MOMENT. Excel feeds a PowerPoint deliverable. */}
        <Section id="cross-app">
          <Container>
            <AnimateOnScroll isVisible={isVisible('cross-header')} data-animate-id="cross-header">
              <CenteredHeader>
                <SectionEyebrow>Where the work connects</SectionEyebrow>
                <SectionTitle component="h2">
                  Your model, deck, and memo, <em>on speaking terms.</em>
                </SectionTitle>
                <SectionSubtitle>
                  Ask once, and the real numbers move between apps, live, without a manual export or reformatting step.
                </SectionSubtitle>
              </CenteredHeader>
            </AnimateOnScroll>

            {/* Excel feeds a PowerPoint deliverable. */}
            <AnimateOnScroll isVisible={isVisible('cross-visual')} delay={0.08} data-animate-id="cross-visual">
              <Box
                sx={{
                  maxWidth: 880,
                  mx: 'auto',
                  mb: { xs: 4, md: 5 },
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 64px 1fr' },
                  alignItems: 'center',
                  gap: { xs: 2, md: 0 },
                }}
              >
                <AppWindow accent={EXCEL_GREEN} appName="Model.xlsx" label="EXCEL">
                  <Box sx={{ borderRadius: '8px', overflow: 'hidden', border: `1px solid ${hair}` }}>
                    {/* Header row: the periods, in Excel green. */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.35fr repeat(3, 1fr)' }}>
                      {['$M', 'FY22', 'FY23', 'FY24E'].map((v, i) => (
                        <ModelCell key={`h-${i}`} header numeric={i > 0}>{v}</ModelCell>
                      ))}
                    </Box>
                    {/* Driver rows. */}
                    {[
                      { label: 'Revenue', vals: ['120.4', '141.7', '168.9'] },
                      { label: 'EBITDA', vals: ['38.1', '47.2', '58.0'] },
                    ].map((row, ri) => (
                      <Box key={`row-${ri}`} sx={{ display: 'grid', gridTemplateColumns: '1.35fr repeat(3, 1fr)' }}>
                        <ModelCell>{row.label}</ModelCell>
                        {row.vals.map((v, ci) => (
                          <ModelCell key={`v-${ci}`} numeric>{v}</ModelCell>
                        ))}
                      </Box>
                    ))}
                    {/* Subtotal: margin, the formula-derived row. */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.35fr repeat(3, 1fr)' }}>
                      <ModelCell subtotal>Margin</ModelCell>
                      {['31.6%', '33.3%', '34.3%'].map((v, ci) => (
                        <ModelCell key={`mg-${ci}`} numeric subtotal>{v}</ModelCell>
                      ))}
                    </Box>
                  </Box>
                  <Box sx={{ mt: 0.9, display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Box sx={{ px: 0.5, py: 0.1, borderRadius: 0.5, fontFamily: '"Inter", sans-serif', fontSize: '0.52rem', fontWeight: 700, letterSpacing: '0.04em', color: EXCEL_GREEN, background: alpha(EXCEL_GREEN, 0.1) }}>
                      fx
                    </Box>
                    <Typography sx={{ fontSize: '0.6rem', fontFamily: '"Inter", sans-serif', color: alpha(INK, 0.55) }}>
                      =EBITDA / Revenue
                    </Typography>
                  </Box>
                  <Typography sx={{ mt: 0.6, fontSize: '0.62rem', color: alpha(INK, 0.5), fontFamily: theme.typography.fontFamily }}>
                    Q3 revenue build &middot; formula-driven, auditable
                  </Typography>
                </AppWindow>

                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center', position: 'relative', height: '100%', justifyContent: 'center' }}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: 2,
                      background: `linear-gradient(90deg, ${alpha(EXCEL_GREEN, 0.5)}, ${alpha(ACCENT, 0.5)})`,
                      borderRadius: 2,
                      overflow: 'visible',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: -3,
                        left: 0,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: ACCENT,
                        boxShadow: `0 0 10px ${alpha(ACCENT, 0.6)}`,
                        animation: 'excelFlow 1.8s linear infinite',
                      },
                      '@keyframes excelFlow': {
                        '0%': { left: '0%', opacity: 0 },
                        '15%': { opacity: 1 },
                        '85%': { opacity: 1 },
                        '100%': { left: '100%', opacity: 0 },
                      },
                      // Decorative, and it loops forever, so it stops entirely
                      // for readers who asked for reduced motion.
                      '@media (prefers-reduced-motion: reduce)': {
                        '&::after': { animation: 'none', left: '50%', opacity: 1 },
                      },
                    }}
                  />
                  <SwapHorizRounded sx={{ position: 'absolute', fontSize: 22, color: alpha(INK, 0.55), background: '#FAF9F7', borderRadius: '50%', p: 0.25 }} />
                </Box>

                <AppWindow accent={ACCENT} appName="Deck.pptx" label="POWERPOINT">
                  <Box sx={{ aspectRatio: '16 / 9', borderRadius: '8px', border: `1px solid ${hair}`, background: '#fff', p: 1.1, display: 'flex', flexDirection: 'column' }}>
                    {/* Slide title + thin accent rule. */}
                    <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.66rem', fontWeight: 700, color: INK, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                      Revenue accelerated to 19% in FY24
                    </Typography>
                    <Box sx={{ mt: 0.5, mb: 0.7, width: 26, height: 2, borderRadius: 1, background: ACCENT }} />
                    {/* Two-part body: takeaway bullets next to a mini bar chart. */}
                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 0.8fr', gap: 0.9, alignItems: 'stretch' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.55 }}>
                        {['Topline up 40% over two years', 'Margin expanding to 34.3%', 'EBITDA compounding at 23%'].map((b, bi) => (
                          <Box key={`b-${bi}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                            <Box sx={{ width: 3, height: 3, borderRadius: '50%', mt: '5px', flexShrink: 0, background: ACCENT }} />
                            <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.55rem', lineHeight: 1.35, color: alpha(INK, 0.72) }}>
                              {b}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      {/* Labelled mini bar chart. */}
                      <Box sx={{ borderRadius: '4px', border: `1px solid ${hair}`, background: alpha(INK, 0.015), p: 0.6, display: 'flex', flexDirection: 'column' }}>
                        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.48rem', fontWeight: 600, letterSpacing: '0.04em', color: alpha(INK, 0.5), mb: 0.4 }}>
                          REVENUE ($M)
                        </Typography>
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
                          {[0.55, 0.72, 1].map((h, hi) => (
                            <Box key={`bar-${hi}`} sx={{ flex: 1, height: `${h * 100}%`, borderRadius: '2px 2px 0 0', background: hi === 2 ? ACCENT : alpha(ACCENT, 0.3) }} />
                          ))}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.3 }}>
                          {['22', '23', '24'].map((y, yi) => (
                            <Typography key={`y-${yi}`} sx={{ flex: 1, textAlign: 'center', fontFamily: '"Inter", sans-serif', fontSize: '0.42rem', color: alpha(INK, 0.45) }}>
                              {y}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Typography sx={{ mt: 0.9, fontSize: '0.62rem', color: alpha(INK, 0.5), fontFamily: theme.typography.fontFamily }}>
                    Slide 4 &middot; built from the model, on your template
                  </Typography>
                </AppWindow>
              </Box>
            </AnimateOnScroll>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: { xs: 2, md: 2.5 }, maxWidth: 1000, mx: 'auto' }}>
              {crossAppSteps.map((s, i) => (
                <AnimateOnScroll key={s.n} isVisible={isVisible(`cross-step-${i}`)} delay={0.1 * (i + 1)} data-animate-id={`cross-step-${i}`}>
                  <Box sx={{ p: { xs: 2.5, md: 3 }, height: '100%', borderRadius: 2, border: `1px solid ${hair}`, backgroundColor: alpha(INK, 0.015) }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: '50%', mb: 1.5, background: INK, color: '#FAF9F7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: theme.typography.fontFamily, fontSize: '0.85rem', fontWeight: 600 }}>
                      {s.n}
                    </Box>
                    <Typography component="h3" sx={{ fontFamily: theme.typography.fontFamily, fontSize: '0.98rem', fontWeight: 600, color: 'text.primary', mb: 0.5, letterSpacing: '-0.01em' }}>
                      {s.title}
                    </Typography>
                    <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '0.84rem', lineHeight: 1.55, color: 'text.secondary' }}>
                      {s.desc}
                    </Typography>
                  </Box>
                </AnimateOnScroll>
              ))}
            </Box>
          </Container>
        </Section>

        {/* 4. WHAT IT DOES IN EXCEL. Benefit cards. */}
        <Section id="excel-capabilities">
          <Container>
            <AnimateOnScroll isVisible={isVisible('excel-cap-header')} data-animate-id="excel-cap-header">
              <CenteredHeader>
                <SectionEyebrow>Inside Excel</SectionEyebrow>
                <SectionTitle component="h2">The operating and LBO model, built in your <em>live workbook.</em></SectionTitle>
                <SectionSubtitle>
                  A side-panel chat that sees and edits the workbook you have open. You ask in plain English; it does the work in your real file.
                </SectionSubtitle>
              </CenteredHeader>
            </AnimateOnScroll>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: { xs: 2, md: 2.5 }, mt: { xs: 1, md: 2 } }}>
              {excelCapabilities.map((cap, i) => (
                <AnimateOnScroll key={cap.title} isVisible={isVisible(`excel-cap-${i}`)} delay={0.06 * (i + 1)} data-animate-id={`excel-cap-${i}`}>
                  <Box sx={{ p: { xs: 2.5, md: 3 }, height: '100%', borderRadius: 2, border: `1px solid ${hair}`, backgroundColor: alpha(INK, 0.015), transition: 'border-color 0.2s ease, transform 0.2s ease', '&:hover': { borderColor: alpha(EXCEL_GREEN, 0.4), transform: 'translateY(-2px)' } }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 1.5, mb: 1.75, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${alpha(EXCEL_GREEN, 0.25)}`, backgroundColor: alpha(EXCEL_GREEN, 0.08), color: EXCEL_GREEN, '& svg': { fontSize: 19 } }}>
                      {cap.icon}
                    </Box>
                    <Typography component="h3" sx={{ fontFamily: theme.typography.fontFamily, fontSize: '1rem', fontWeight: 600, color: 'text.primary', mb: 0.75, letterSpacing: '-0.01em' }}>
                      {cap.title}
                    </Typography>
                    <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '0.84rem', lineHeight: 1.55, color: 'text.secondary' }}>
                      {cap.desc}
                    </Typography>
                  </Box>
                </AnimateOnScroll>
              ))}
            </Box>
          </Container>
        </Section>

        {/* 5. HOW IT WORKS. The shared capability showcase (eyebrow, title,
            subtitle, and four animated cards are all internal to the component). */}
        <CapabilityShowcase isVisible={isVisible} loop app="excel" />

        {/* 6. CORE CAPABILITIES. The shared bento, tuned for Excel. */}
        <CoreCapabilities app="excel" isVisible={isVisible} />

        {/* 7. CONNECTS TO. The same data-sources globe carried across surfaces. */}
        <Section id="excel-connectors">
          <Container>
            <AnimateOnScroll isVisible={isVisible('excel-connectors-header')} data-animate-id="excel-connectors-header">
              <CenteredHeader>
                <SectionEyebrow>Connects to</SectionEyebrow>
                <SectionTitle component="h2">The model, built from your <em>data sources.</em></SectionTitle>
                <SectionSubtitle>
                  Pulls from terminals, filings, and your own files, in your tenant. Works alongside Bloomberg,
                  FactSet, and Capital IQ via your exports today; direct connectors are on the roadmap.
                </SectionSubtitle>
              </CenteredHeader>
            </AnimateOnScroll>
            <AnimateOnScroll isVisible={isVisible('excel-connectors-globe')} delay={0.1} data-animate-id="excel-connectors-globe">
              <ConnectorGlobe surface="excel" />
            </AnimateOnScroll>
          </Container>
        </Section>

        {/* 8. WHO IT IS FOR + EXAMPLE PROMPTS. */}
        <Section id="excel-personas">
          <Container>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '0.9fr 1.1fr' }, gap: { xs: 4, md: 7 }, alignItems: 'start' }}>
              <AnimateOnScroll isVisible={isVisible('personas')} data-animate-id="personas">
                <Box>
                  <SectionEyebrow>Who it is for</SectionEyebrow>
                  <SectionTitle component="h2" sx={{ textAlign: 'left', mt: 1.5, mb: 2 }}>
                    Built for the people who <em>live</em> in Private Equity deliverables.
                  </SectionTitle>
                  <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '0.95rem', lineHeight: 1.65, color: 'text.secondary' }}>
                    Private equity, growth equity, and consulting teams tired of moving numbers
                    between a model, a deck, and a memo by hand. Built for the rigor Private Equity expects.
                  </Typography>
                  {/* Maturity hedge: honest about surface age, same trust move
                      as the Security page's precision-over-impressive line. */}
                  <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '0.85rem', lineHeight: 1.6, color: 'text.secondary', opacity: 0.75, mt: 1.5 }}>
                    PowerPoint is Restack's most mature surface; Excel and Word run on the same agent and are newer.
                  </Typography>
                </Box>
              </AnimateOnScroll>

              <AnimateOnScroll isVisible={isVisible('prompts')} delay={0.1} data-animate-id="prompts">
                <Box>
                  <Typography component="h3" sx={{ ...eyebrowSx, mb: 1.5 }}>Things to ask it</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {excelPrompts.map((p, i) => (
                      <PromptChip key={`x-${i}`} accent={EXCEL_GREEN} theme={theme} hair={hair}>{p}</PromptChip>
                    ))}
                    {crossPrompts.map((p, i) => (
                      <PromptChip key={`c-${i}`} accent={ACCENT} theme={theme} hair={hair}>{p}</PromptChip>
                    ))}
                  </Box>
                </Box>
              </AnimateOnScroll>
            </Box>
          </Container>
        </Section>

        {/* 9. FAQ. Answers pulled by id from FAQ_REPOSITORY, so this page and the
            support widget can never drift apart. */}
        <Section id="faq">
          <Container>
            <AnimateOnScroll isVisible={isVisible('faq-header')} data-animate-id="faq-header">
              <CenteredHeader>
                <SectionEyebrow>Questions</SectionEyebrow>
                <SectionTitle component="h2">Before you <em>trial it</em>.</SectionTitle>
              </CenteredHeader>
            </AnimateOnScroll>
            <AnimateOnScroll isVisible={isVisible('faq-list')} data-animate-id="faq-list">
              <FaqList entries={EXCEL_FAQ} theme={theme} />
            </AnimateOnScroll>
          </Container>
        </Section>

        {/* 10. CLOSING CTA. Same wording as the hero, with the risk reversal
             directly under it and cross-surface links kept as text. */}
        <Section id="excel-cta" sx={{ pb: { xs: 8, md: 12 } }}>
          <Container>
            <AnimateOnScroll isVisible={isVisible('excel-cta')} data-animate-id="excel-cta">
              <Box
                sx={{
                  p: { xs: 4, md: 6 },
                  borderRadius: '20px',
                  textAlign: 'center',
                  border: `1px solid ${hair}`,
                  background: `linear-gradient(135deg, ${alpha(ACCENT, 0.06)} 0%, ${alpha('#FAF9F7', 0.6)} 100%)`,
                }}
              >
                <Typography component="h2" sx={{ fontFamily: theme.typography.fontFamily, fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 600, letterSpacing: '-0.02em', color: 'text.primary', mb: 1.5 }}>
                  Bring the model you are building this week.
                </Typography>
                <Typography sx={{ maxWidth: 560, mx: 'auto', mb: 3, fontFamily: theme.typography.fontFamily, fontSize: '0.95rem', lineHeight: 1.6, color: 'text.secondary' }}>
                  We will show you Restack building it formula-driven and cited inside your live workbook, then
                  carrying the numbers into the deck and the memo.
                </Typography>
                <CtaRow label={PRIMARY_CTA_LABEL} theme={theme} />
                <Typography sx={{ maxWidth: 560, mx: 'auto', mt: 3, fontFamily: theme.typography.fontFamily, fontSize: '0.82rem', lineHeight: 1.6, color: 'text.secondary' }}>
                  {REFUND_COPY}
                </Typography>
                {/* Cross-surface navigation stays a text link, not a second
                    button, so the band keeps exactly one primary action. */}
                <Typography sx={{ mt: 2, fontFamily: theme.typography.fontFamily, fontSize: '0.82rem', color: 'text.secondary' }}>
                  Also for <SurfaceLink to="/product">PowerPoint</SurfaceLink> and <SurfaceLink to="/word">Word</SurfaceLink>.
                </Typography>
              </Box>
            </AnimateOnScroll>
          </Container>
        </Section>
      </Box>

      <Footer />
    </LandingContainer>
  );
}

// ---------------------------------------------------------------------------
// Small presentational helpers (local to this page).
// ---------------------------------------------------------------------------

function AppWindow({ accent, appName, label, children }) {
  return (
    <Box
      sx={{
        borderRadius: '14px',
        overflow: 'hidden',
        border: `1px solid ${alpha('#2E140A', 0.1)}`,
        background: '#fff',
        boxShadow: '0 14px 34px rgba(46, 20, 10, 0.12)',
      }}
    >
      {/* Title bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1.25, py: 0.9, borderBottom: `1px solid ${alpha('#2E140A', 0.07)}`, background: alpha(accent, 0.08) }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: accent }} />
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: alpha('#2E140A', 0.7), fontFamily: 'inherit' }}>{appName}</Typography>
        <Box sx={{ ml: 'auto', px: 0.7, py: 0.15, borderRadius: 1, background: alpha(accent, 0.14), color: accent, fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.08em' }}>{label}</Box>
      </Box>
      <Box sx={{ p: 1.5 }}>{children}</Box>
    </Box>
  );
}

// A single cell in the banker-grade model fragment. Header cells carry the
// Excel green; the subtotal (margin) row is tinted and bolded; numerics are
// right-aligned with tabular figures.
function ModelCell({ children, header, numeric, subtotal }) {
  const green = '#217346';
  return (
    <Box
      sx={{
        px: 0.7,
        py: 0.45,
        fontFamily: '"Inter", sans-serif',
        fontFeatureSettings: '"tnum" 1',
        fontVariantNumeric: 'tabular-nums',
        fontSize: '0.6rem',
        fontWeight: header || subtotal ? 600 : 500,
        color: header
          ? '#fff'
          : subtotal
          ? green
          : alpha('#2E140A', numeric ? 0.78 : 0.62),
        background: header
          ? green
          : subtotal
          ? alpha(green, 0.08)
          : 'transparent',
        borderTop: subtotal ? `1px solid ${alpha(green, 0.3)}` : 'none',
        textAlign: numeric ? 'right' : 'left',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {children}
    </Box>
  );
}

function PromptChip({ children, accent, theme, hair }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 1.75,
        py: 1.1,
        borderRadius: '12px',
        border: `1px solid ${hair}`,
        backgroundColor: alpha('#2E140A', 0.015),
        fontFamily: theme.typography.fontFamily,
        fontSize: '0.85rem',
        lineHeight: 1.5,
        color: 'text.secondary',
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', mt: '7px', flexShrink: 0, background: accent }} />
      {children}
    </Box>
  );
}
